import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useTheme } from "@/lib/theme";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";

function Row({ label, hint, children, testid }) {
  return (
    <div className="grid md:grid-cols-[260px_1fr] gap-6 py-5 border-b border-border last:border-0" data-testid={testid}>
      <div>
        <div className="text-sm font-medium">{label}</div>
        {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
      </div>
      <div className="flex items-center gap-3">{children}</div>
    </div>
  );
}

export default function Settings() {
  const { t, lang, setLang } = useI18n();
  const { theme, setTheme } = useTheme();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const hydrated = useRef(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/settings")
      .then((r) => { if (!cancelled && !hydrated.current) { hydrated.current = true; setS(r.data); } })
      .catch(() => {
        if (!cancelled && !hydrated.current) {
          hydrated.current = true;
          setS({
            default_folder: "AdLibrary", parallel_limit: 3, language: "pt",
            theme: "light", naming_template: "{brand}_{campaign}_{index}",
            auto_retry: true, max_retries: 3,
          });
        }
      });
    return () => { cancelled = true; };
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings", { ...s, language: lang, theme });
      setS(data);
      toast.success(t("settings.saved"));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!s) return null;
  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));

  return (
    <div className="space-y-8">
      <header>
        <div className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground">Preferences</div>
        <h1 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight">{t("settings.title")}</h1>
        <p className="mt-1.5 text-sm text-muted-foreground">{t("settings.subtitle")}</p>
      </header>

      <Card>
        <CardContent className="p-6">
          <Row label={t("settings.defaultFolder")} hint="Downloads/{folder}/Marca/Campanha/…" testid="row-default-folder">
            <Input value={s.default_folder} onChange={(e) => set("default_folder", e.target.value)} data-testid="input-default-folder" />
          </Row>
          <Row label={t("settings.parallelLimit")} hint="1 – 20" testid="row-parallel-limit">
            <Input type="number" min={1} max={20} value={s.parallel_limit} onChange={(e) => set("parallel_limit", parseInt(e.target.value || "1", 10))} data-testid="input-parallel-limit" />
          </Row>
          <Row label={t("settings.namingTemplate")} hint="{brand} {campaign} {index} {date}" testid="row-naming-template">
            <Input value={s.naming_template} onChange={(e) => set("naming_template", e.target.value)} data-testid="input-naming-template" />
          </Row>
          <Row label={t("settings.autoRetry")} testid="row-auto-retry">
            <Switch checked={!!s.auto_retry} onCheckedChange={(v) => set("auto_retry", v)} data-testid="switch-auto-retry" />
          </Row>
          <Row label={t("settings.maxRetries")} testid="row-max-retries">
            <Input type="number" min={0} max={10} value={s.max_retries} onChange={(e) => set("max_retries", parseInt(e.target.value || "0", 10))} data-testid="input-max-retries" />
          </Row>
          <Row label={t("settings.language")} testid="row-language">
            <Select value={lang} onValueChange={setLang}>
              <SelectTrigger className="w-40" data-testid="select-language"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pt">Português</SelectItem>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="es">Español</SelectItem>
              </SelectContent>
            </Select>
          </Row>
          <Row label={t("settings.theme")} testid="row-theme">
            <Select value={theme} onValueChange={setTheme}>
              <SelectTrigger className="w-40" data-testid="select-theme"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="light">{t("settings.themeLight")}</SelectItem>
                <SelectItem value="dark">{t("settings.themeDark")}</SelectItem>
              </SelectContent>
            </Select>
          </Row>

          <div className="pt-6 flex justify-end">
            <Button onClick={save} disabled={busy} data-testid="settings-save">
              {busy ? t("common.loading") : t("settings.save")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
